import { useMakeFilteredUrlFromPath } from './useFilter';
import { NavGroup, isRouteNavItem } from './useNavGroups';
import { useCan } from './useRbac';

/**
 * Calculate route specificity for sorting.
 * More specific routes (with more literal segments) should match first.
 * e.g., "/:company?/:project?/fuel/dashboard" is more specific than "/:company?/:project?/dashboard"
 */
export const getRouteSpecificity = (path: string): number => {
  const segments = path.split('/').filter(Boolean);
  // Count literal segments (not starting with :)
  const literalSegments = segments.filter((s) => !s.startsWith(':')).length;
  // Total segments as tiebreaker
  return literalSegments * 100 + segments.length;
};

export const useRoutes = (routes: NavGroup[]) => {
  const make = useMakeFilteredUrlFromPath();
  const can = useCan();
  const canAccessRoute = (
    route: { permission?: string; routePermission?: string },
    groupVisible: boolean
  ) => {
    const canByItemPermission = route.permission
      ? can(route.permission)
      : false;
    const canByRoutePermission = route.routePermission
      ? can(route.routePermission)
      : false;

    if (groupVisible) {
      return !route.permission || canByItemPermission;
    }

    return canByItemPermission || canByRoutePermission;
  };

  return (
    routes
      .flatMap(({ navItems, ...group }) => {
        const groupVisible = can(`sidebar:${group.title}`);

        return navItems
          .flatMap((item) => {
            // External links (e.g. the PostHog project) have no route.
            const subRoutes = item.items
              ? item.items.filter(isRouteNavItem).map((subItem) => ({
                  ...subItem,
                  breadcrumbs: subItem.breadcrumbs ?? [
                    { title: group.title },
                    {
                      title: item.label ?? item.name,
                      // A parent with its own page (e.g. Checklists →
                      // Plantillas) is a link; dropdown-only parents have
                      // no path and stay plain text.
                      href: item.path ? make(item.path) : undefined,
                    },
                    {
                      title: subItem.label ?? subItem.name,
                      href: make(subItem.path),
                    },
                  ],
                  to: make(subItem.path),
                  __groupVisible: groupVisible,
                }))
              : [];
            // A collapsible item (no `path`) only ever registers its
            // sub-items. A definite item registers itself too, even when it
            // also carries `items` for a "…" overflow menu (e.g. Checklists
            // → Plantillas) — see NavItemDefinite in useNavGroups.tsx.
            const ownRoute = item.path
              ? [{ ...item, __groupVisible: groupVisible }]
              : [];
            return [...ownRoute, ...subRoutes];
          })
          .flatMap((item) => ({
            ...item,
            breadcrumbs: item.breadcrumbs ?? [
              { title: group.title },
              {
                title: item.label ?? item.name,
                href: item.path ? make(item.path) : undefined,
              },
            ],
          }));
      })
      .filter((route) => !!route.path && !route.skipRoute)
      .filter((route) => canAccessRoute(route, route.__groupVisible))
      .map((route) => ({
        name: route.name,
        path: route.path,
        breadcrumbs: route.breadcrumbs,
        exact: route.exact,
        component: route.component,
        permission: route.permission,
        layout: route.layout,
        trackPageview: route.trackPageview,
      }))
      // Sort by specificity: more specific routes (more literal segments) first
      .sort((a, b) => getRouteSpecificity(b.path) - getRouteSpecificity(a.path))
  );
};
